import { Box, Button, Flex, Grid, GridItem, Image, Text, useColorMode } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { assets } from "../../../assets/assets";
import { EmailInviteTypeDto, useGetMarketDialogueInviteQuery } from "../../../autogen/bff-api";
import { AlreadyAccepted } from "../AlreadyAccepted";
import { InviteNotice } from "../InviteNotice";
import {
  isInviteGone,
  isInviteRateLimited,
  isWrongInviteLink,
  MarketDialogueInviteFacts,
} from "../MarketDialogueInviteFacts";
import { LoginDrawer } from "./LoginDrawer";
import { SignupDrawer } from "./SignupDrawer/SignupDrawer";
import { SignupOrLogin } from "./SignupOrLogin";

interface Props {
  inviteId: string;
  secret: string;
}

export const MarketDialogueEmailInvite = ({ inviteId, secret }: Props) => {
  const { t } = useTranslation();
  const theme = useColorMode();
  const [showSignup, setShowSignup] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const { data, error, refetch, isFetching } = useGetMarketDialogueInviteQuery({ inviteId, secret });

  if (error) {
    if (isInviteRateLimited(error)) {
      return (
        <InviteNotice title={t("Too many attempts")}>
          <Text>{t("Wait a few minutes, then open the link from your email again.")}</Text>
        </InviteNotice>
      );
    }
    if (isInviteGone(error)) {
      return (
        <InviteNotice title={t("The market dialogue no longer exists")}>
          <Text>{t("The contracting authority has removed it, so there is nothing to answer.")}</Text>
        </InviteNotice>
      );
    }
    if (isWrongInviteLink(error)) {
      return (
        <InviteNotice title={t("This invitation link is not valid")}>
          <Text>{t("Open the most recent link from your email, or ask the contracting authority to invite you again.")}</Text>
        </InviteNotice>
      );
    }
    return (
      <InviteNotice title={t("We could not fetch the invitation")}>
        <Button mt="4" colorScheme="teal" onClick={() => void refetch()} isLoading={isFetching}>
          {t("Try again")}
        </Button>
      </InviteNotice>
    );
  }
  if (!data) return <div>{t("Loading")}...</div>;
  if (data.accepted) return <AlreadyAccepted />;

  const invitationContext = { emailInviteId: inviteId, emailInviteType: EmailInviteTypeDto.MarketDialogue, secret };

  return (
    <Box
      width="full"
      height="100vh"
      bgGradient="linear(to-br, smTealShadow, smBackground, smBackground, smBackground, smBlueShadow)"
    >
      {showSignup && (
        <SignupDrawer
          asOrg={true}
          isOpen={showSignup}
          onClose={() => setShowSignup(false)}
          invitationContext={invitationContext}
        />
      )}
      {showLogin && (
        <LoginDrawer isOpen={showLogin} onClose={() => setShowLogin(false)} invitationContext={invitationContext} />
      )}
      <Grid
        gridTemplateColumns={"1fr minmax(0, 520px) 1fr"}
        height="100%"
        gridTemplateRows={"1fr minmax(420px, auto) 2fr"}
        templateAreas={`
                "left top right"
                "left login right"
                "left bottom right"
                `}
      >
        <GridItem area={"login"} rounded="md" backgroundColor="smBackground" boxShadow="smAuthCard" padding={10}>
          <Flex w="100%" h="100%" flexDirection="column">
            <Flex paddingTop="20px" paddingBottom="35px">
              <Image src={theme.colorMode === "dark" ? assets.logo_dark : assets.logo} alt="SourceMagnet" h="30px" mt="10px" />
            </Flex>
            <MarketDialogueInviteFacts invite={data} />
            <Text fontSize="sm" mt="20px" color="smMuted">
              {t("Register your company to answer, or log in if it already has an account. You are taken to the market dialogue afterwards.")}
            </Text>
            <SignupOrLogin onSignup={() => setShowSignup(true)} onLogin={() => setShowLogin(true)} />
          </Flex>
        </GridItem>
      </Grid>
    </Box>
  );
};
